import { FindMessageThreadsQueryVariables } from "gql/graphql";
import stableHash from "stable-hash";

interface MessageThreadFindQuery {
  variables: FindMessageThreadsQueryVariables;
  count: number;
  cacheKey: string;
}

class MessageThreadFindQueryRegistry {
  private registry = new Map<string, MessageThreadFindQuery>();

  private static normalizeFilters(variables: FindMessageThreadsQueryVariables) {
    return variables.input.searchFilterList
      .map((filter) => ({
        ...filter,
        stringListValue: filter.stringListValue?.slice().sort(),
        objectIdListValue: filter.objectIdListValue?.slice().sort(),
      }))
      .sort((a, b) => {
        if (a.type === b.type) return 0;
        return a.type < b.type ? -1 : 1;
      });
  }

  private static serialize(variables: FindMessageThreadsQueryVariables) {
    return stableHash({
      searchFilterList:
        MessageThreadFindQueryRegistry.normalizeFilters(variables),
      sortType: variables.input.sortType,
    });
  }

  register(cacheKey: string, variables: FindMessageThreadsQueryVariables) {
    const key = MessageThreadFindQueryRegistry.serialize(variables);
    const entry = this.registry.get(key);

    if (entry) {
      entry.count += 1;
    } else {
      this.registry.set(key, {
        variables,
        count: 1,
        cacheKey,
      });
    }
    return key;
  }

  unregister(variables: FindMessageThreadsQueryVariables) {
    const key = MessageThreadFindQueryRegistry.serialize(variables);
    const entry = this.registry.get(key);
    if (!entry) return;

    entry.count -= 1;
    if (entry.count === 0) {
      this.registry.delete(key);
    }
  }

  getAllVariables(): FindMessageThreadsQueryVariables[] {
    return Array.from(this.registry.values()).map((e) => e.variables);
  }

  clear() {
    this.registry.clear();
  }

  getAll() {
    return Array.from(this.registry.values());
  }

  debugSnapshot() {
    return Array.from(this.registry.entries()).map(
      ([key, { variables, count }]) => ({
        key,
        count,
        variables,
      }),
    );
  }
}

export const messageThreadFindQueryRegistry =
  new MessageThreadFindQueryRegistry();
